import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Divider,
} from 'antd';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { userService, permissionService, departmentService } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';

const { Title } = Typography;
const { Option } = Select;

const ROLE_COLORS = {
  admin: 'red',
  manager: 'blue',
  analyst: 'green',
  finance: 'gold',
  viewer: 'default',
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [userDepartments, setUserDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const { can, user: currentUser } = usePermissions();

  const canEdit = can('users', 'edit');
  const canDelete = can('users', 'delete');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await permissionService.getRoles();
      setRoles(data);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await departmentService.getAll();
      setDepartments(data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const fetchUserDepartments = async (userId) => {
    try {
      const data = await userService.getDepartments(userId);
      setUserDepartments(data.map(m => m.department_id));
    } catch (error) {
      console.error('Failed to load user departments:', error);
      setUserDepartments([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchDepartments();
  }, []);

  const handleCreateEdit = async (values) => {
    try {
      const { departments: selectedDepts, ...userData } = values;

      if (editingUser) {
        await userService.update(editingUser.id, userData);
        // Update departments if changed
        if (selectedDepts !== undefined) {
          await userService.updateDepartments(editingUser.id, selectedDepts || []);
        }
        message.success('User updated successfully');
      } else {
        const newUser = await userService.create(userData);
        // Set departments for new user
        if (selectedDepts && selectedDepts.length > 0) {
          await userService.updateDepartments(newUser.id, selectedDepts);
        }
        message.success('User created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingUser(null);
      setUserDepartments([]);
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await userService.delete(userId);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditModal = async (user) => {
    setEditingUser(user);
    // Fetch user's current departments
    await fetchUserDepartments(user.id);
    form.setFieldsValue({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
    });
    setModalVisible(true);
  };

  // Set departments in form after userDepartments is loaded
  useEffect(() => {
    if (editingUser && userDepartments.length >= 0) {
      form.setFieldsValue({ departments: userDepartments });
    }
  }, [userDepartments, editingUser, form]);

  const openCreateModal = () => {
    setEditingUser(null);
    setUserDepartments([]);
    form.resetFields();
    form.setFieldsValue({ role: 'viewer', is_active: true, departments: [] });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: (a, b) => a.full_name.localeCompare(b.full_name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={ROLE_COLORS[role] || 'default'}>
          {role?.toUpperCase() || 'VIEWER'}
        </Tag>
      ),
      filters: roles.map((r) => ({ text: r.toUpperCase(), value: r })),
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Admin',
      dataIndex: 'is_admin',
      key: 'is_admin',
      render: (isAdmin) => (isAdmin ? <Tag color="purple">Admin</Tag> : null),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canEdit && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
          )}
          {canDelete && record.id !== currentUser?.id && (
            <Popconfirm
              title="Delete this user?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete"
              okType="danger"
            >
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>User Management</Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
              Refresh
            </Button>
            {canEdit && (
              <Button type="primary" icon={<UserAddOutlined />} onClick={openCreateModal}>
                Add User
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'Create User'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          setUserDepartments([]);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateEdit}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input disabled={!!editingUser} />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item name="role" label="Role">
            <Select>
              {roles.map((role) => (
                <Option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_active" label="Status">
            <Select>
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item
            name="departments"
            label={
              <span>
                <TeamOutlined style={{ marginRight: 8 }} />
                Departments
              </span>
            }
            tooltip="Select the departments this user belongs to"
          >
            <Select
              mode="multiple"
              placeholder="Select departments"
              allowClear
              optionFilterProp="children"
            >
              {departments.map((dept) => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
